import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { fetchWrapper } from "../helpers";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { ensureInstagramCompliant, remuxMovMp4IfPossible } from "../helpers/videoEncodingHelper";
import { store } from '../store';

const name = "post";
const createInitialState = () => {
  return {
    post: null,
    loading: false,
    generating: false,
  };
};

const createExtraActions = () => {

  const baseUrl = `${process.env.REACT_APP_API_URL}`;

  const getPost = () => {
    return createAsyncThunk(
      `${name}/post`,
      async (contentPlanId) =>
        await fetchWrapper.get(`${baseUrl}/post/actual/${contentPlanId}`)
    );
  };

  const generatePost = () => {
    return createAsyncThunk(`${name}/postCreate`, async ({ contentPlanId, reqBody }) => {
      return await fetchWrapper.post(
        `${baseUrl}/post/generate/${contentPlanId}`, reqBody
      );
    });
  };

  const generateImage = () => {
    return createAsyncThunk(
      `${name}/generateImage`,
      async ({ postId, contentPlanId, referenceImages }, { rejectWithValue }) => {
        try {
          const url = `${baseUrl}/post/generate-image/${postId}`;

          const formData = new FormData();

          if (referenceImages && referenceImages.length > 0) {
            referenceImages.forEach(image => {
              formData.append('referenceImages', image);
            });
          }

          await fetchWrapper.post(url, formData);

          return await fetchWrapper.get(
            `${baseUrl}/post/actual/${contentPlanId}`
          );
        } catch (error) {
          console.error("Image generation action failed:", error);
          return rejectWithValue(error.toString());
        }
      }
    );
  };

  const removeAllImages = () => {
    return createAsyncThunk(
      `${name}/removeAllImages`,
      async ({ postId, contentPlanId }) => {
        await fetchWrapper.delete(`${baseUrl}/post/remove-all-images/${postId}`);
          return { cleared: true };
      }
    );
  };

  const removeImage = () => {
    return createAsyncThunk(
        `${name}/removeImage`,
        async ({ postId, contentPlanId, removeIndex, url }) => {
            const qs = new URLSearchParams();
            if (typeof removeIndex === 'number') qs.set('index', String(removeIndex));
            if (url) qs.set('url', url);
            const query = qs.toString();
            await fetchWrapper.delete(
                `${baseUrl}/post/remove-image/${postId}${query ? `?${query}` : ''}`
            )
            return { removeIndex, url };
        }
    )
  }

  const updateImages = () => {
    return createAsyncThunk(
      `${name}/updateImages`,
      async ({ postId, contentPlanId, images }) => {
        const formData = new FormData();

        images.forEach((image, index) => {
          if (image.imageFile instanceof File) {
            formData.append(`images[${index}]`, image.imageFile, image.imageFile.name);
          } else if (
            image.imageUrl &&
            typeof image.imageUrl === 'string' &&
            !image.imageUrl.startsWith('blob:') &&
            !image.imageUrl.startsWith('data:')
          ) {
            formData.append(`imageUrls[${index}]`, image.imageUrl);
          }

          if (image.params) {
            formData.append(`params[${index}]`, JSON.stringify(image.params));
          }

          if (image.id) {
            formData.append(`imageIds[${index}]`, image.id);
          }
        });

        await fetchWrapper.put(
          `${baseUrl}/post/update-images/${postId}`,
          formData,
          { token: store.getState().auth.user?.accessToken }
        );

        const result = await fetchWrapper.get(
          `${baseUrl}/post/actual/${contentPlanId}`,
          { token: store.getState().auth.user?.accessToken }

        );

        return result;
      }
    );
  };

  const replaceImage = () => {
      return createAsyncThunk(
          `${name}/replaceImage`,
          async ({ postId, contentPlanId, index, file, imageUrl, imageBase64 }) => {
              const form = new FormData();
              form.append('index', String(index));
              if (file instanceof File) form.append('image', file, file.name);
              else if (typeof imageUrl === 'string') form.append('imageUrl', imageUrl);
              else if (typeof imageBase64 === 'string') form.append('imageBase64', imageBase64);
              else throw new Error('No image payload provided');
              await fetchWrapper.put(`${baseUrl}/post/update-one-image/${postId}`, form, { token: store.getState().auth.user?.accessToken });
              return await fetchWrapper.get(
                  `${baseUrl}/post/actual/${contentPlanId}`,
                  { token: store.getState().auth.user?.accessToken }
              );
          }
      )
    }

  const uploadImage = () => {
    return createAsyncThunk(
      `${name}/uploadImage`,
      async ({ postId, contentPlanId, images, mode = "append" }, { getState, rejectWithValue }) => {
        const messages = {
          success: {
            ru: "Изображение(я) успешно загружены",
            en: "Images uploaded successfully",
            kk: "Суреттер сәтті жүктелді",
          },
          badCount: {
            ru: "Можно загрузить максимум 10 изображений",
            en: "You can upload up to 10 images",
            kk: "Ең көбі 10 сурет жүктеуге болады",
          },
          badPerFile: {
            ru: "Каждый файл должен быть ≤ 8 МБ",
            en: "Each file must be ≤ 8MB",
            kk: "Әр файл 8 МБ-тан аспау керек",
          },
          badTotal: {
            ru: "Общий размер партии должен быть ≤ 80 МБ",
            en: "Total batch size must be ≤ 80MB",
            kk: "Жалпы көлем 80 МБ-тан аспау керек",
          },
          serverTotal: {
            ru: "Слишком много изображений в сумме (максимум 10)",
            en: "Too many images in total (max 10)",
            kk: "Жалпы сурет саны тым көп (ең көбі 10)",
          },
          unknown: {
            ru: "Ошибка загрузки изображений",
            en: "Image upload failed",
            kk: "Суретті жүктеу қатесі",
          },
        };

        const PER_FILE_LIMIT = 8 * 1024 * 1024;
        const TOTAL_LIMIT = 80 * 1024 * 1024;
        const MAX_IMAGES = 10;

        const state = getState();
        const existing = state.post?.post?.images || [];
        const existingCount = Array.isArray(existing) ? existing.length : 0;

        if (!images?.length) {
          return rejectWithValue(messages.unknown.en);
        }

        if (images.length > MAX_IMAGES) {
          return rejectWithValue(messages.badCount.en);
        }

        if (mode === "append") {
          if (existingCount + images.length > MAX_IMAGES) {
            return rejectWithValue(messages.serverTotal.en);
          }
        } else if (mode === "replace") {
          if (images.length > MAX_IMAGES) {
            return rejectWithValue(messages.badCount.en);
          }
        }

        let total = 0;
        for (const image of images) {
          if (image.size > PER_FILE_LIMIT) {
            return rejectWithValue(messages.badPerFile.en);
          }
          total += image.size || 0;
        }
        if (total > TOTAL_LIMIT) {
          return rejectWithValue(messages.badTotal.en);
        }

        const formData = new FormData();
        images.forEach((image) => formData.append("images", image));

        const url = `${baseUrl}/post/upload-image/${postId}?mode=${mode}`;

        try {
          await fetchWrapper.post(url, formData);
          const fresh = await fetchWrapper.get(`${baseUrl}/post/actual/${contentPlanId}`);
          return fresh;
        } catch (e) {
          const errCode = e?.payload?.error || e?.error || "";
          const map = {
            per_image_too_large: messages.badPerFile.en,
            too_many_images_total: messages.serverTotal.en,
            too_many_images_batch: messages.badCount.en,
            no_valid_images_processed_error: messages.unknown.en,
            unexpected_error: messages.unknown.en,
            forbidden_not_owner: "You don’t have permission to modify this post.",
          };
          return rejectWithValue(map[errCode] || messages.unknown.en);
        }
      }
    );
  };

  const uploadVideo = () => {
    return createAsyncThunk(
      `${name}/uploadVideo`,
      async ({ postId, contentPlanId, video }) => {
        const messages = {
          success: {
            ru: "Видео успешно загружено",
            en: "Video successfully uploaded",
            kk: "Бейне сәтті жүктелді",
          },
          error: {
            ru: "ERROR! Ошибка при загрузке видео, возможно размер файлов превышает 200mb либо продолжительность видео превышает 60 секунд",
            en: "ERROR! Error when uploading video, maybe file size exceeds 200mb or video duration exceeds 60 seconds",
            kk: "ERROR! Бейнені жүктеу кезінде қате, мүмкін файлдардың өлшемі 200 Мб-тан асады немесе бейненің ұзақтығы 60 секундтан асады",
          },
        };
        // Quick guardrails in case the file is too large :VIDEOFILELIMITER
        if (video.size > 200 * 1024 * 1024) {
          message.warning({
            en: 'Video is too large',
            ru: 'Слишком большой файл',
            kk: 'Файл тым үлкен'
          }[localStorage.getItem('ln')])
        }
        // Try client remux (HEVC is skipped and will be handled server-side)
        let fileToSend = video;
        try {
          const hide = message.loading({ en: 'Optimizing video...', ru: 'Оптимизация видео', kk: 'Құрылғыда оңтайландырылды' }[localStorage.getItem('ln')]);
          let { file, changed, reason } = await remuxMovMp4IfPossible(video);
          const encodedFile = await ensureInstagramCompliant(file);
          hide();
          file = encodedFile.file;
          if (changed) {
            fileToSend = file;
            console.info('[remux] sending MP4:', fileToSend.name, fileToSend.type, fileToSend.size);
            message.info({ en: 'Optimized on device', ru: 'Оптимизировано на устройстве', kk: 'Құрылғыда оңтайландырылды' }[localStorage.getItem('ln')]);
          } else if (reason?.startsWith('unsupported-codec-')) {
            // Likely HEVC/HDR - will be handled server-side
            // Or we could optimize short clips here. It will take more time though, and Safari doesn't like it.
            message.info({ en: `Optimization failed: ${reason}`, ru: `Оптимизация не удалась: ${reason}`, kk: `Оңтайландыру сәтсіз аяқталды: ${reason}` }[localStorage.getItem('ln')]);
          }
        } catch (e) {
          // Silent fallback to original
          console.warn('Client remux failed, using original file... ', e);
        }

        const formData = new FormData();
        // images.forEach((image) => formData.append("images", image));
        formData.append("video", fileToSend);
        await fetchWrapper.post(
          `${baseUrl}/post/upload-video/${postId}`,
          formData
        ).then((response) => {
          if (response) {
            message.success(messages.success[localStorage.getItem('ln')] || messages.success.en);
          } else {
            message.error(messages.error[localStorage.getItem('ln')] || messages.error.en);
          }
        }).catch((err) => {
          message.error(messages.error[localStorage.getItem('ln')] || messages.error.en);
          console.error(err);
        });

        return await fetchWrapper.get(
          `${baseUrl}/post/actual/${contentPlanId}`
        );
      }
    );
  };
  const removeVideo = () => {
    return createAsyncThunk(
      `${name}/removeVideo`,
      async ({ postId, contentPlanId }) => {
        await fetchWrapper.delete(`${baseUrl}/post/remove-video/${postId}`);
        return await fetchWrapper.get(
          `${baseUrl}/post/actual/${contentPlanId}`
        );
      }
    );
  };

  const updatePost = () => {
    return createAsyncThunk(
      `${name}/updatePost`,
      async ({ postId, contentPlanId, postData }) => {
        await fetchWrapper.put(`${baseUrl}/post/update/${postId}`, postData);
        return await fetchWrapper.get(
          `${baseUrl}/post/actual/${contentPlanId}`
        );
      }
    );
  };

  const regeneratePost = () => {
    return createAsyncThunk(
      `${name}/regeneratePost`,
      async ({ postId, contentPlanId, postData }) => {
        await fetchWrapper.post(`${baseUrl}/post/regenerate/${contentPlanId}`, postData);
        return await fetchWrapper.get(
          `${baseUrl}/post/actual/${contentPlanId}`,
        )
      }
    )
  }

  return {
    getPost: getPost(),
    generatePost: generatePost(),
    generateImage: generateImage(),
    updateImages: updateImages(),
    uploadImage: uploadImage(),
    uploadVideo: uploadVideo(),
    removeVideo: removeVideo(),
    removeImage: removeImage(),
    updatePost: updatePost(),
    regeneratePost: regeneratePost(),
      removeAllImages: removeAllImages(),
      replaceImage: replaceImage(),
  };
};

const createExtraReducers = (builder) => {
  const getPost = () => {
    const { pending, fulfilled, rejected } = extraActions.getPost;
    builder.addCase(pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fulfilled, (state, action) => {
      if (action.payload?.status) {
        state.loading = false;
        state.generating = true;
      } else {
        state.post = action.payload;
        state.loading = false;
        state.generating = false;
      }
    });
    builder.addCase(rejected, (state, action) => {
      state.post = null;
      state.loading = false;
    });
  };

  const uploadImagePost = () => {
    const { pending, fulfilled, rejected } = extraActions.uploadImage;
    builder.addCase(pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fulfilled, (state, action) => {
      state.post = action.payload;
      state.loading = false;
    });
    builder.addCase(rejected, (state, action) => {
      // сохраняем текущий пост, только сбрасываем загрузку
      state.loading = false;
    });
  };

  const generateImagePost = () => {
    const { pending, fulfilled, rejected } = extraActions.generateImage;
    builder.addCase(pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fulfilled, (state, action) => {
      state.post = action.payload;
      state.loading = false;
    });
    builder.addCase(rejected, (state, action) => {
      state.loading = false;
    });
  };

  const updateImagesPost = () => {
    const { pending, fulfilled, rejected } = extraActions.updateImages;
    builder.addCase(pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fulfilled, (state, action) => {
      state.post = action.payload;
      state.loading = false;
    });
    builder.addCase(rejected, (state, action) => {
      // не затираем state.post, чтобы UI не падал на CreatePost
      state.loading = false;
    });
  };

    const replaceImageReducer = () => {
        const { pending, fulfilled, rejected } = extraActions.replaceImage;
        builder.addCase(pending, (state) => { state.loading = true; });
        builder.addCase(fulfilled, (state, action) => {
            state.post = action.payload;
            state.loading = false;
        });
        builder.addCase(rejected, (state) => { state.loading = false; });
    };

  const removeImagePost = () => {
    const { pending, fulfilled, rejected } = extraActions.removeImage;
    // builder.addCase(pending, (state) => {
      // state.loading = true;
    // });
    builder.addCase(fulfilled, (state, action) => {
        const { removeIndex, url } = action.payload || {};
        if (!state.post) { state.loading = false; return; }
        const imgs = Array.isArray(state.post.images) ? state.post.images : [];
        const logos = Array.isArray(state.post.imagesWithLogo) ? state.post.imagesWithLogo : [];
        let idx = typeof removeIndex === 'number' ? removeIndex : imgs.findIndex(u => u === url);
        if (idx >= 0 && idx < imgs.length) {
                 imgs.splice(idx, 1);
                if (idx < logos.length) logos.splice(idx, 1);
        }
        state.post = { ...state.post, images: [...imgs], imagesWithLogo: [...logos] };
        state.loading = false;
    });
    builder.addCase(rejected, (state) => {
      state.loading = false;
    });
  };

    const removeAllImagesPost = () => {
        const { pending, fulfilled, rejected } = extraActions.removeAllImages;
        // builder.addCase(pending, (state) => {
        //     state.loading = true;
        // });
        builder.addCase(fulfilled, (state, action) => {
            if (state.post) {
                state.post = {...state.post, images: [], imagesWithLogo: []};
            }
            state.loading = false;
        });
        builder.addCase(rejected, (state, action) => {
            state.loading = false;
        });
    };

  const createPost = () => {
    const { pending, fulfilled, rejected } = extraActions.generatePost;
    builder.addCase(pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fulfilled, (state, action) => {
      state.loading = false;
      state.generating = true;
    });
    builder.addCase(rejected, (state, action) => {
      state.loading = false;
    });
  };

  const updatePostHandler = () => {
    const { pending, fulfilled, rejected } = extraActions.updatePost;
    builder.addCase(pending, (state) => {
      //state.loading = true;
    });
    builder.addCase(fulfilled, (state, action) => {
        state.post.post = action.payload.post;
        state.post.hashtags = action.payload.hashtags;
        state.post.media = action.payload.media;
        state.post.collaborators.collaborators = action.payload.collaborators.collaborators;
        //state.loading = false;
    });
    builder.addCase(rejected, (state, action) => {
      state.loading = false;
    });
  };

  const regeneratePostHandler = () => {
    const { pending, fulfilled, rejected } = extraActions.regeneratePost;
    builder.addCase(pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fulfilled, (state) => {
      // After we fire /post/regenerate/:id the backend starts async generation.
      // We mark "generating" to show a spinner/notice while waiting for polling/getPost.
      state.loading = false;
      state.generating = true;
    });
    builder.addCase(rejected, (state) => {
      state.loading = false;
    });
  };
  const uploadVideoPost = () => {
    const { pending, fulfilled, rejected } = extraActions.uploadVideo;
    builder.addCase(pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fulfilled, (state, action) => {
      state.post = action.payload;   // update post with new video
      state.loading = false;
    });
    builder.addCase(rejected, (state) => {
      state.loading = false;         // keep old post so UI doesn’t blank out
    });
  };

  const removeVideoPost = () => {
    const { pending, fulfilled, rejected } = extraActions.removeVideo;
    builder.addCase(pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fulfilled, (state, action) => {
      state.post = action.payload;   // post without video
      state.loading = false;
    });
    builder.addCase(rejected, (state) => {
      state.loading = false;
    });
  };

  getPost();
  createPost();
  uploadImagePost();
  generateImagePost();
  updateImagesPost();
  removeImagePost();
  updatePostHandler();
  regeneratePostHandler();
  uploadVideoPost();
  removeVideoPost();
  removeAllImagesPost();
  replaceImageReducer();
};

const extraActions = createExtraActions();
const slice = createSlice({
  name: name,
  initialState: createInitialState(),
  reducers: {
    resetPost: (state) => {
      state.post = null;
      state.loading = true; // покажем спиннер пока ждём новый fetch
      state.generating = false;
    }
  },
  extraReducers: (builder) => {
    const extraReducers = createExtraReducers(builder);
  },
});

export const postActions = { ...slice.actions, ...extraActions };
export const postReducer = slice.reducer;
